import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    DropdownMenu,
    Button
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    PlusIcon,
    StarIcon,
    UserIcon,
    BellIcon,
    FileIcon
} from "../../anatomy/icons";

const NAME = "DropdownMenu";

const USAGE_CODE = `const [lastAction, setLastAction] = useState("");

<DropdownMenu
    title="Project"
>
    <DropdownMenu.Trigger>
        <Button
            onPress={() => undefined}
            variant="outline"
            title="Project"
            type="neutral"
        />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content>
        <DropdownMenu.Item
            onPress={() => setLastAction("New task")}
            title="New task"
            shortcut="mod+N"
            icon={PlusIcon}
        />
        <DropdownMenu.Separator/>
        <DropdownMenu.Item
            onPress={() => setLastAction("Delete")}
            shortcut="mod+backspace"
            title="Delete project"
            type="danger"
        />
    </DropdownMenu.Content>
</DropdownMenu>`;

const ITEM_CODE = `<DropdownMenu.Item
    onPress={() => setSyncCount(syncCount + 1)}
    subTitle="Stays open when chosen"
    isCloseOnSelect={false}
    title="Sync now"
/>
<DropdownMenu.Item
    disabledReason="Only project owners can archive"
    isDisabled={true}
    title="Archive"
/>
<DropdownMenu.Item
    shortcut="mod+backspace"
    title="Delete"
    type="danger"
/>`;

const ALIGNMENT_CODE = `const [isStatusBar, setIsStatusBar] = useState(true);

const [isSidePanel, setIsSidePanel] = useState(false);

<DropdownMenu
    title="My account"
>
    <DropdownMenu.Trigger>
        <Button
            onPress={() => undefined}
            title="My account"
            variant="outline"
            type="neutral"
        />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content>
        <DropdownMenu.Label
            title="My account"
        />
        <DropdownMenu.Item
            shortcut="mod+P"
            title="Profile"
        />
        <DropdownMenu.Item
            title="Billing"
        />
        <DropdownMenu.Item
            shortcut="mod+,"
            title="Settings"
        />
        <DropdownMenu.Separator/>
        <DropdownMenu.CheckboxItem
            onChange={setIsStatusBar}
            isChecked={isStatusBar}
            title="Status bar"
        />
        <DropdownMenu.CheckboxItem
            onChange={setIsSidePanel}
            isChecked={isSidePanel}
            title="Side panel"
        />
        <DropdownMenu.Separator/>
        <DropdownMenu.Item
            title="API keys"
        />
        <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger
                title="Share"
            />
            <DropdownMenu.SubContent>
                <DropdownMenu.Item
                    title="Copy link"
                />
                <DropdownMenu.Item
                    title="Send by email"
                />
            </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
        <DropdownMenu.Separator/>
        <DropdownMenu.Item
            title="Delete account"
            type="danger"
        />
    </DropdownMenu.Content>
</DropdownMenu>`;

const CONTROLLED_CODE = `const [isOpen, setIsOpen] = useState(false);

<DropdownMenu
    onOpenChange={setIsOpen}
    isOpen={isOpen}
>
    <DropdownMenu.Trigger>
        Options
    </DropdownMenu.Trigger>
    <DropdownMenu.Content>
        ...
    </DropdownMenu.Content>
</DropdownMenu>`;

const SIDES = [
    "top",
    "right",
    "bottom",
    "left"
] as const;

const ALIGNS = [
    "start",
    "center",
    "end"
] as const;

const COLUMNS = [
    "name",
    "status",
    "amount"
] as const;

const SORTS = [
    "name",
    "date",
    "size"
] as const;

const THEMES = [
    "system",
    "light",
    "dark"
] as const;

const MEMBERS = [
    "Ada Lovelace",
    "Grace Hopper",
    "Alan Turing"
];

const INITIAL_COLUMNS: Array<(typeof COLUMNS)[number]> = [
    "name",
    "status"
];

const LOAD_DELAY = 1200;

const STRETCH_STYLE = {
    alignSelf: "stretch"
} as const;

const getAllColumnsState = (count: number): boolean | "partially" => {
    if(count === COLUMNS.length) {
        return true;
    }

    return count === 0 ? false : "partially";
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("dropdownMenuPage.itemsTitle"),
        id: "items"
    },
    {
        title: t("dropdownMenuPage.choicesTitle"),
        id: "choices"
    },
    {
        title: t("dropdownMenuPage.submenusTitle"),
        id: "submenus"
    },
    {
        title: t("dropdownMenuPage.alignmentTitle"),
        id: "alignment"
    },
    {
        title: t("dropdownMenuPage.placementTitle"),
        id: "placement"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DropdownMenuPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        lastAction,
        setLastAction
    ] = useState("");

    const [
        syncCount,
        setSyncCount
    ] = useState(0);

    const [
        visibleColumns,
        setVisibleColumns
    ] = useState(INITIAL_COLUMNS);

    const [
        isStatusBar,
        setIsStatusBar
    ] = useState(true);

    const [
        isSidePanel,
        setIsSidePanel
    ] = useState(false);

    const [
        sort,
        setSort
    ] = useState("name");

    const [
        theme,
        setTheme
    ] = useState<(typeof THEMES)[number]>("system");

    const [
        assignStatus,
        setAssignStatus
    ] = useState<"loading" | "ready">("ready");

    useEffect(() => {
        if(assignStatus !== "loading") {
            return;
        }

        const timer = window.setTimeout(() => setAssignStatus("ready"), LOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        assignStatus
    ]);

    const columnLabels = {
        amount: t("sample.columnAmount"),
        status: t("sample.columnStatus"),
        name: t("sample.columnName")
    };

    const sortLabels = {
        date: t("dropdownMenuPage.sortDate"),
        size: t("dropdownMenuPage.sortSize"),
        name: t("dropdownMenuPage.sortName")
    };

    const themeLabels = {
        system: t("dropdownMenuPage.themeSystem"),
        light: t("dropdownMenuPage.themeLight"),
        dark: t("dropdownMenuPage.themeDark")
    };

    const handleAction = (title: string) => setLastAction(title);

    const handleSync = () => setSyncCount(syncCount + 1);

    const handleThemeChange = (value: string) => setTheme(THEMES.find((key) => key === value) ?? "system");

    const handleAllColumnsChange = (isChecked: boolean) => {
        setVisibleColumns(isChecked ? [
            ...COLUMNS
        ] : []);
    };

    const handleColumnChange = (column: (typeof COLUMNS)[number], isChecked: boolean) => {
        setVisibleColumns(COLUMNS.filter((key) => key === column ? isChecked : visibleColumns.includes(key)));
    };

    const handleAssignOpenChange = (isOpen: boolean) => {
        if(isOpen) {
            setAssignStatus("loading");
        }
    };

    const renderPlacementMenu = ({
        isStretched = false,
        align,
        title,
        side
    }: {
        align?: (typeof ALIGNS)[number];
        side?: (typeof SIDES)[number];
        isStretched?: boolean;
        title: string;
    }) => <DropdownMenu
        title={t("dropdownMenuPage.options")}
        align={align}
        side={side}
        key={title}
    >
        <DropdownMenu.Trigger
            style={isStretched ? STRETCH_STYLE : undefined}
        >
            <Button
                spreadBehaviour={isStretched ? "stretch" : "baseline"}
                onPress={() => undefined}
                variant="outline"
                type="neutral"
                title={title}
            />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
            widthBehaviour={isStretched ? "trigger" : "content"}
        >
            <DropdownMenu.Item
                title={t("sample.item1")}
                icon={UserIcon}
            />
            <DropdownMenu.Item
                title={t("sample.item2")}
            />
            <DropdownMenu.Separator/>
            <DropdownMenu.Item
                title={t("sample.item3")}
            />
        </DropdownMenu.Content>
    </DropdownMenu>;

    return <Article>
        <ComponentHeader
            lead={t("dropdownMenuPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("dropdownMenuPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("dropdownMenuPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={lastAction ? t("dropdownMenuPage.lastAction", [
                    lastAction
                ]) : t("dropdownMenuPage.overviewCaption")}
            >
                <DropdownMenu
                    title={t("dropdownMenuPage.project")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.project")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Item
                            onPress={() => handleAction(t("dropdownMenuPage.newTask"))}
                            title={t("dropdownMenuPage.newTask")}
                            shortcut="mod+N"
                            icon={PlusIcon}
                        />
                        <DropdownMenu.Item
                            onPress={() => handleAction(t("dropdownMenuPage.duplicate"))}
                            title={t("dropdownMenuPage.duplicate")}
                            shortcut="mod+D"
                            icon={FileIcon}
                        />
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            onPress={() => handleAction(t("dropdownMenuPage.deleteProject"))}
                            title={t("dropdownMenuPage.deleteProject")}
                            shortcut="mod+backspace"
                            type="danger"
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("dropdownMenuPage.itemsTitle")}
            id="items"
        >
            <Paragraph>
                {t("dropdownMenuPage.itemsText")}
            </Paragraph>
            <Preview>
                <DropdownMenu
                    title={t("dropdownMenuPage.actions")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.actions")}
                            onPress={() => undefined}
                            variant="tonal"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Item
                            subTitle={t("dropdownMenuPage.duplicateSub")}
                            title={t("dropdownMenuPage.duplicate")}
                            shortcut="mod+D"
                            icon={FileIcon}
                        />
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.favourite")}
                            icon={StarIcon}
                        />
                        <DropdownMenu.Item
                            subTitle={syncCount ? t("dropdownMenuPage.syncCount", [
                                syncCount
                            ]) : t("dropdownMenuPage.syncHint")}
                            title={t("dropdownMenuPage.sync")}
                            isCloseOnSelect={false}
                            onPress={handleSync}
                        />
                        <DropdownMenu.Item
                            disabledReason={t("dropdownMenuPage.archiveReason")}
                            title={t("dropdownMenuPage.archive")}
                            isDisabled={true}
                        />
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.delete")}
                            shortcut="mod+backspace"
                            type="danger"
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
            <CodeBlock
                code={ITEM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dropdownMenuPage.choicesTitle")}
            id="choices"
        >
            <Paragraph>
                {t("dropdownMenuPage.choicesText")}
            </Paragraph>
            <Preview
                caption={t("dropdownMenuPage.choicesCaption")}
            >
                <DropdownMenu
                    title={t("dropdownMenuPage.columns")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.columns")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.CheckboxItem
                            isChecked={getAllColumnsState(visibleColumns.length)}
                            title={t("dropdownMenuPage.allColumns")}
                            onChange={handleAllColumnsChange}
                        />
                        <DropdownMenu.Separator/>
                        {
                            COLUMNS.map((column) => <DropdownMenu.CheckboxItem
                                onChange={(isChecked) => handleColumnChange(column, isChecked)}
                                isChecked={visibleColumns.includes(column)}
                                title={columnLabels[column]}
                                key={column}
                            />)
                        }
                    </DropdownMenu.Content>
                </DropdownMenu>
                <DropdownMenu
                    title={t("dropdownMenuPage.sortBy")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.sortBy")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.RadioGroup
                            title={t("dropdownMenuPage.sortBy")}
                            onChange={setSort}
                            value={sort}
                        >
                            {
                                SORTS.map((key) => <DropdownMenu.RadioItem
                                    title={sortLabels[key]}
                                    value={key}
                                    key={key}
                                />)
                            }
                        </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
        </Section>
        <Section
            title={t("dropdownMenuPage.submenusTitle")}
            id="submenus"
        >
            <Paragraph>
                {t("dropdownMenuPage.submenusText")}
            </Paragraph>
            <Preview>
                <DropdownMenu
                    title={t("dropdownMenuPage.options")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.options")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Group
                            title={t("dropdownMenuPage.account")}
                        >
                            <DropdownMenu.Item
                                title={t("sample.item1")}
                                icon={UserIcon}
                            />
                            <DropdownMenu.Item
                                title={t("dropdownMenuPage.notifications")}
                                icon={BellIcon}
                            />
                        </DropdownMenu.Group>
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Label
                            title={t("dropdownMenuPage.preferences")}
                        />
                        <DropdownMenu.Sub>
                            <DropdownMenu.SubTrigger
                                title={t("dropdownMenuPage.theme")}
                                valueText={themeLabels[theme]}
                            />
                            <DropdownMenu.SubContent>
                                <DropdownMenu.RadioGroup
                                    onChange={handleThemeChange}
                                    value={theme}
                                >
                                    {
                                        THEMES.map((key) => <DropdownMenu.RadioItem
                                            title={themeLabels[key]}
                                            value={key}
                                            key={key}
                                        />)
                                    }
                                </DropdownMenu.RadioGroup>
                            </DropdownMenu.SubContent>
                        </DropdownMenu.Sub>
                        <DropdownMenu.Sub>
                            <DropdownMenu.SubTrigger
                                title={t("dropdownMenuPage.share")}
                            />
                            <DropdownMenu.SubContent>
                                <DropdownMenu.Item
                                    title={t("dropdownMenuPage.copyLink")}
                                />
                                <DropdownMenu.Item
                                    title={t("dropdownMenuPage.sendEmail")}
                                />
                            </DropdownMenu.SubContent>
                        </DropdownMenu.Sub>
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            title={t("sample.item3")}
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
        </Section>
        <Section
            title={t("dropdownMenuPage.alignmentTitle")}
            id="alignment"
        >
            <Paragraph>
                {t("dropdownMenuPage.alignmentText")}
            </Paragraph>
            <Preview
                caption={t("dropdownMenuPage.alignmentCaption")}
            >
                <DropdownMenu
                    title={t("dropdownMenuPage.alignmentAccount")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.alignmentAccount")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Label
                            title={t("dropdownMenuPage.alignmentAccount")}
                        />
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.alignmentProfile")}
                            shortcut="mod+P"
                        />
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.alignmentBilling")}
                        />
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.alignmentSettings")}
                            shortcut="mod+,"
                        />
                        <DropdownMenu.Separator/>
                        <DropdownMenu.CheckboxItem
                            title={t("dropdownMenuPage.alignmentStatusBar")}
                            onChange={setIsStatusBar}
                            isChecked={isStatusBar}
                        />
                        <DropdownMenu.CheckboxItem
                            title={t("dropdownMenuPage.alignmentSidePanel")}
                            onChange={setIsSidePanel}
                            isChecked={isSidePanel}
                        />
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.alignmentApiKeys")}
                        />
                        <DropdownMenu.Sub>
                            <DropdownMenu.SubTrigger
                                title={t("dropdownMenuPage.share")}
                            />
                            <DropdownMenu.SubContent>
                                <DropdownMenu.Item
                                    title={t("dropdownMenuPage.copyLink")}
                                />
                                <DropdownMenu.Item
                                    title={t("dropdownMenuPage.sendEmail")}
                                />
                            </DropdownMenu.SubContent>
                        </DropdownMenu.Sub>
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.alignmentDeleteAccount")}
                            type="danger"
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
            <CodeBlock
                code={ALIGNMENT_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("dropdownMenuPage.placementTitle")}
            id="placement"
        >
            <Paragraph>
                {t("dropdownMenuPage.placementText")}
            </Paragraph>
            <Preview
                caption={t("dropdownMenuPage.sideCaption")}
            >
                {
                    SIDES.map((side) => renderPlacementMenu({
                        title: side,
                        side
                    }))
                }
            </Preview>
            <Preview
                caption={t("dropdownMenuPage.alignCaption")}
            >
                {
                    ALIGNS.map((align) => renderPlacementMenu({
                        title: align,
                        align
                    }))
                }
            </Preview>
            <Preview
                caption={t("dropdownMenuPage.widthCaption")}
                isStacked={true}
            >
                {
                    renderPlacementMenu({
                        title: t("dropdownMenuPage.viewMenu"),
                        isStretched: true
                    })
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("dropdownMenuPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("dropdownMenuPage.statesCaption")}
            >
                <DropdownMenu
                    title={t("dropdownMenuPage.disabled")}
                >
                    <DropdownMenu.Trigger
                        isDisabled={true}
                    >
                        <Button
                            title={t("dropdownMenuPage.disabled")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Item
                            title={t("sample.item1")}
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
                <DropdownMenu
                    title={t("dropdownMenuPage.assignTo")}
                    onOpenChange={handleAssignOpenChange}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.assignTo")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        {
                            assignStatus === "loading" ?
                                <DropdownMenu.Status/>
                            :
                                MEMBERS.map((member) => <DropdownMenu.Item
                                    title={member}
                                    key={member}
                                />)
                        }
                    </DropdownMenu.Content>
                </DropdownMenu>
                <DropdownMenu
                    title={t("dropdownMenuPage.labels")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            title={t("dropdownMenuPage.labels")}
                            onPress={() => undefined}
                            variant="outline"
                            type="neutral"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.Empty
                            description={t("dropdownMenuPage.noLabelsHint")}
                            title={t("dropdownMenuPage.noLabels")}
                        />
                        <DropdownMenu.Separator/>
                        <DropdownMenu.Item
                            title={t("dropdownMenuPage.newLabel")}
                            icon={PlusIcon}
                        />
                    </DropdownMenu.Content>
                </DropdownMenu>
            </Preview>
            <CodeBlock
                code={CONTROLLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("dropdownMenuPage.a11yTrigger"),
                    t("dropdownMenuPage.a11yMenu"),
                    t("dropdownMenuPage.a11yKeyboard"),
                    t("dropdownMenuPage.a11ySubmenu"),
                    t("dropdownMenuPage.a11yFocus"),
                    t("dropdownMenuPage.a11yDisabled"),
                    t("dropdownMenuPage.a11yTouch"),
                    t("dropdownMenuPage.a11yWarn")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "\"bottom\" | \"top\" | \"left\" | \"right\"",
                        description: t("dropdownMenuPage.propSide"),
                        defaultValue: "\"bottom\"",
                        name: "side"
                    },
                    {
                        description: t("dropdownMenuPage.propAlign"),
                        type: "\"start\" | \"center\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("dropdownMenuPage.propIsOpen"),
                        type: "boolean",
                        name: "isOpen"
                    },
                    {
                        description: t("dropdownMenuPage.propInitialIsOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("dropdownMenuPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("dropdownMenuPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("dropdownMenuPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dropdownMenuPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    DropdownMenu.Trigger
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propTriggerChildren"),
                        type: "ReactElement | string",
                        name: "children"
                    },
                    {
                        description: t("dropdownMenuPage.propTriggerIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.Content
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partContentText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propWidthBehaviour"),
                        type: "\"content\" | \"trigger\"",
                        defaultValue: "\"content\"",
                        name: "widthBehaviour"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.Item
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propItemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dropdownMenuPage.propItemIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("dropdownMenuPage.propItemSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("dropdownMenuPage.propItemShortcut"),
                        type: "Array<string> | string",
                        name: "shortcut"
                    },
                    {
                        description: t("dropdownMenuPage.propItemType"),
                        type: "\"neutral\" | \"danger\"",
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("dropdownMenuPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("dropdownMenuPage.propItemDisabledReason"),
                        name: "disabledReason",
                        type: "string"
                    },
                    {
                        description: t("dropdownMenuPage.propItemIsCloseOnSelect"),
                        name: "isCloseOnSelect",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("dropdownMenuPage.propItemTypeaheadText"),
                        name: "typeaheadText",
                        type: "string"
                    },
                    {
                        description: t("dropdownMenuPage.propItemOnPress"),
                        type: "() => void",
                        name: "onPress"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dropdownMenuPage.itemNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    DropdownMenu.CheckboxItem
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partCheckboxText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propCheckboxTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dropdownMenuPage.propCheckboxIsChecked"),
                        type: "boolean | \"partially\"",
                        defaultValue: "false",
                        name: "isChecked"
                    },
                    {
                        description: t("dropdownMenuPage.propCheckboxOnChange"),
                        type: "(isChecked: boolean) => void",
                        name: "onChange"
                    },
                    {
                        description: t("dropdownMenuPage.propCheckboxIsCloseOnSelect"),
                        name: "isCloseOnSelect",
                        defaultValue: "false",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.RadioGroup
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partRadioText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propRadioGroupValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("dropdownMenuPage.propRadioGroupOnChange"),
                        type: "(value: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("dropdownMenuPage.propRadioGroupTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.RadioItem
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partRadioItemText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propRadioItemValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("dropdownMenuPage.propRadioItemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dropdownMenuPage.propRadioItemOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("dropdownMenuPage.propRadioItemIsCloseOnSelect"),
                        name: "isCloseOnSelect",
                        defaultValue: "true",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.SubTrigger
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partSubTriggerText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propSubTriggerTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("dropdownMenuPage.propSubTriggerValueText"),
                        name: "valueText",
                        type: "string"
                    },
                    {
                        description: t("dropdownMenuPage.propItemIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("dropdownMenuPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    DropdownMenu.Label
                </InlineCode>
                {" "}
                {t("dropdownMenuPage.partLabelText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("dropdownMenuPage.propLabelTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("dropdownMenuPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default DropdownMenuPage;
